import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import keyboardKeyNames from '../../../../helpers/enums/keyboardKeyNames.js';

// This hook implements navigation through items using keys
// It keeps TAB key as usual, NON-CIRCULAR navigation
// It also implements onBLur and OnFocus to update the index avoiding jumps
// for example: when the user exits or returns to the menu using tabs after moving to another any component using the mouse

/*
Use Instructions: 
	* set containerRef as ref into the container you want to apply this behavior
	* if you want to prevent focus jumps when use user uses a mix of mouse and keyboard to navigate, plese set onBlur and onFocus into the container.
	* set handleKeyDown as onKeyDown event into the items
	* please set innerRef in every item following this example 
		innerRef={(el) => {
			setItemRefToIndex(el, itemIndex);
		}}
 */

const STEP = 1;
const PAGE = 10;
const FIRST = 0;
const UNSELECTED = -1;
const FOCUSABLE_TAB_INDEX = '0';
const NON_FOCUSABLE_TAB_INDEX = '-1';
function useArrowKeysNavigation(itemsCount) {
  let pageJump = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : PAGE;
  const [selectedItemIndex, setSelectedItemIndex] = useState(UNSELECTED);
  const itemsRef = useRef([]);
  const containerRef = useRef(null);
  const LAST = useMemo(() => itemsCount - 1, [itemsCount]);
  const moveUp = useCallback(function (event) {
    let shouldKeepInside = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    return setSelectedItemIndex(prev => {
      const next = prev - STEP;
      if (next < FIRST) {
        if (shouldKeepInside) {
          event.preventDefault();
          return LAST;
        }
        return -STEP;
      }
      event.preventDefault();
      return next;
    });
  }, [LAST]);
  const moveDown = useCallback(function (event) {
    let shouldKeepInside = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    return setSelectedItemIndex(prev => {
      const next = prev + STEP;
      if (next > LAST) {
        if (shouldKeepInside) {
          event.preventDefault();
          return FIRST;
        }
        return -STEP;
      }
      event.preventDefault();
      return next;
    });
  }, [LAST]);
  const handleKeyDown = useCallback(event => {
    switch (event.key) {
      case keyboardKeyNames.ARROW_DOWN:
        event.preventDefault();
        moveDown(event);
        break;
      case keyboardKeyNames.ARROW_UP:
        event.preventDefault();
        moveUp(event);
        break;
      case keyboardKeyNames.HOME:
        setSelectedItemIndex(FIRST);
        break;
      case keyboardKeyNames.END:
        setSelectedItemIndex(LAST);
        break;
      case keyboardKeyNames.PAGE_DOWN:
        if (itemsCount <= selectedItemIndex + pageJump) {
          setSelectedItemIndex(LAST);
        } else {
          setSelectedItemIndex(selectedItemIndex + pageJump);
        }
        break;
      case keyboardKeyNames.PAGE_UP:
        if (selectedItemIndex - pageJump < FIRST) {
          setSelectedItemIndex(FIRST);
        } else {
          setSelectedItemIndex(selectedItemIndex - pageJump);
        }
        break;
    }
  }, [moveDown, LAST, itemsCount, selectedItemIndex, pageJump, moveUp]);
  useEffect(() => {
    if (itemsRef.current[selectedItemIndex]) {
      itemsRef.current[selectedItemIndex]?.focus();
    }
  }, [selectedItemIndex]);
  const setItemsFocusable = (isFocusable, item) => {
    itemsRef.current.forEach(x => {
      if (x !== item) {
        x?.setAttribute('tabIndex', isFocusable ? FOCUSABLE_TAB_INDEX : NON_FOCUSABLE_TAB_INDEX);
      } else {
        x?.setAttribute('tabIndex', FOCUSABLE_TAB_INDEX);
      }
    });
  };
  const onBlur = e => {
    if (containerRef.current && !containerRef.current?.contains(e.relatedTarget)) {
      setItemsFocusable(true);
      setSelectedItemIndex(UNSELECTED);
    }
  };
  const onFocus = e => {
    if (containerRef.current !== e.relatedTarget) {
      setItemsFocusable(false, e.target);
      setSelectedItemIndex(itemsRef.current.findIndex(x => x === e.target));
    }
  };
  const setItemRefToIndex = (itemRef, index) => {
    itemsRef.current[index] = itemRef;
  };
  return {
    onBlur,
    onFocus,
    handleKeyDown,
    setSelectedItemIndex,
    containerRef,
    setItemRefToIndex
  };
}

export { useArrowKeysNavigation as default };
//# sourceMappingURL=useArrowKeysNavigation.js.map
